/* eslint-disable react-hooks/exhaustive-deps */
import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import {
    addDoc,
    collection,
    deleteDoc,
    doc,
    getDoc,
    getDocs,
    onSnapshot,
    orderBy,
    query,
    serverTimestamp,
    updateDoc,
} from "firebase/firestore";
import { auth, db } from "../../../firebase";
import { Option } from "./option.type";
import { useLocationState } from "../../Utils";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";


export const OptionApi = createApi({
    reducerPath: "optionApi",
    tagTypes: ["option", "options"],
    baseQuery: fakeBaseQuery(),
    endpoints: (builder) => ({
        addOrEditOption: builder.mutation({
            async queryFn({ id, ...data }) {
                try {
                    const user = auth.currentUser;
                    if (!user) {
                        return { error: { message: "Aucun utilisateur connecté." } };
                    }
                    const userId = user?.uid;
                    if (!id) {
                        const docRef = await addDoc(collection(db, "options"), { ...data, createdAt: serverTimestamp(), createdBy: userId, updatedAt: serverTimestamp() });
                        return {
                            data: {
                                id: docRef.id,
                                message: "Option enregistrée.",
                                ...data,
                            },
                        };

                    } else {
                        // Mettre à jour Firestore
                        const docRef = doc(db, "options", id);
                        await updateDoc(docRef, { ...data, updatedAt: serverTimestamp() });

                        return {
                            data: {
                                success: true,
                                message:
                                    "Option mise à jour.",
                            },
                        };
                    }
                } catch (error: any) {
                    return { error: { status: "CUSTOM", message: error.message } };
                }
            },
            invalidatesTags: ["option", "options"],
        }),
        getOptions: builder.query({
            queryFn: async () => {
                const optionsRef = collection(db, "options");
                let q = query(optionsRef, orderBy("createdAt", "desc"));
                const snapshot = await getDocs(q);

                const options = await Promise.all(
                    snapshot.docs.map(async (docSnap) => {
                        const option = docSnap.data();

                        return {
                            id: docSnap.id,
                            ...option
                        };
                    })
                );

                return { data: options as unknown as Option[] };
            },
            // Gestion du temps réel avec Firestore et `onSnapshot`
            async onCacheEntryAdded(
                { status },
                { updateCachedData, cacheDataLoaded, cacheEntryRemoved }
            ) {
                let unsubscribe;

                try {
                    await cacheDataLoaded;

                    const optionsRef = collection(db, "options");
                    let q = query(optionsRef, orderBy("createdAt", "desc"));

                    unsubscribe = onSnapshot(q, async (snapshot) => {
                        const options = await Promise.all(
                            snapshot.docs.map(async (docSnap) => {
                                const option = docSnap.data();

                                return {
                                    id: docSnap.id,
                                    ...option
                                };
                            })
                        );

                        // Met à jour le cache RTK Query avec les nouvelles données
                        updateCachedData(() => options as unknown as Option[]);
                    });
                } catch (error) {
                    console.error("Erreur de souscription:", error);
                }

                // Nettoyer l'écouteur Firestore quand le cache est supprimé
                await cacheEntryRemoved;
                if (unsubscribe) unsubscribe();
            },
            providesTags: ["options"],
        }),
        getOptionById: builder.query({
            queryFn: async (optionId) => {
                try {
                    const optionRef = doc(db, "options", optionId);
                    const optionSnap = await getDoc(optionRef);

                    if (!optionSnap.exists()) {
                        return {
                            error: { status: 404, message: "Option introuvable !" },
                        };
                    }

                    return { data: { id: optionSnap.id, ...optionSnap.data() } };
                } catch (error: any) {
                    return { error: { status: 500, message: error.message } };
                }
            },
            providesTags: ["option"],
        }),
        // Supprimer
        deleteOption: builder.mutation({
            queryFn: async (uid) => {
                try {
                    const docRef = doc(db, "options", uid);
                    await deleteDoc(docRef);
                    return {
                        data: { uid, message: "Option supprimée.", success: true },
                    };
                } catch (error: any) {
                    return { error: { status: "DELETE_ERROR", message: error.message } };
                }
            },
            invalidatesTags: ["option", "options"],
        }),
    }),
});

export const {
    useAddOrEditOptionMutation,
    useGetOptionsQuery,
    useLazyGetOptionByIdQuery,
} = OptionApi;

export function useOptionFromLocation(): [
    Option,
    boolean,
    string,
    (id: string) => any
] {
    const itemState = useLocationState<Option | undefined>(undefined);
    const [item, setItem] = useState(itemState);
    const { id } = useParams<{ id: string }>();
    const [findById, { data, isError, isLoading }] = useLazyGetOptionByIdQuery();
    const navigate = useNavigate();

    useEffect(() => {
        // console.log("is id", id)
        if (id && id !== undefined) {
            // console.log("id", id)
            findById(id as string);
        }
    }, [id]);
    useEffect(() => {
        if (isError && !itemState) {
            navigate(-1);
        }
    }, [isError]);
    useEffect(() => {
        if (data) {
            setItem(data as Option);
        }
    }, [data]);

    return [item as Option & { bio: string }, isLoading, id as string, findById];
}

export const useOption = (key: string, def: string | null = null): string | null => {
    const { data = [] } = useGetOptionsQuery({});
    return useMemo(() => {
        const option = data && data?.find((op: Option) => op?.key === key);
        if (key?.endsWith("_image")) {
            return option?.image_value || def;
        }
        else {
            return option?.value || def;
        }
    }, [data, key, def]);
}